import { useLeafletContext } from "@react-leaflet/core";
import { DomEvent, LeafletEvent, PopupOptions } from "leaflet";
import React, { ReactNode, useEffect, useState } from "react";
import { Popup } from "react-leaflet";

interface Props extends PopupOptions {
  // A thunk rather than a node, so the content isn't even constructed until the popup is needed.
  readonly renderContent: () => ReactNode;
}

/*
 * Re-fires the click that mounted the popup, once the real popup has bound itself to the layer.
 *
 * Leaflet only starts opening a popup on click after `bindPopup`, so the click that triggered the
 * mount would otherwise be lost. Replaying it on the layer (not on the map -- `fire` without
 * propagation stays on the layer) lets Leaflet's own handler open the popup at the clicked point,
 * exactly as it did before the popup became lazy. Rendered after the `<Popup>` so that its effect,
 * which does the binding, has already run.
 */
const ReplayClick = ({ event }: { readonly event: LeafletEvent }) => {
  const { overlayContainer } = useLeafletContext();

  useEffect(() => {
    overlayContainer?.fire("click", event);
  }, [overlayContainer, event]);

  return null;
};

/*
 * A `<Popup>` that costs nothing until its parent layer is first clicked.
 *
 * react-leaflet's `<Popup>` constructs an `L.Popup` and registers two map-level listeners as soon as
 * it mounts, whether or not it is ever opened -- one popup and two listeners per annotation (about
 * 18,000 of each on the ~18,000-annotation image profiled for LUC-253). Its children are already deferred until first open, so the cost is the
 * shell, which is why the whole `<Popup>` is deferred here and not just its content.
 *
 * Until the first click this renders nothing and holds a single one-shot `click` listener on the
 * parent layer. After it, the real popup mounts, binds itself, and Leaflet handles every later click.
 *
 * Must be rendered as a child of a layer (`Circle`, `Polygon`, `Polyline`, `GeoJSON`, ...). Do not use
 * for popups that are opened programmatically (e.g. `MultilineAnnotation`, opened from the Celiac
 * table), which have to be bound before anything is clicked.
 */
const LazyPopup = ({ renderContent, ...popupOptions }: Props) => {
  const { overlayContainer } = useLeafletContext();
  const [firstClick, setFirstClick] = useState<LeafletEvent | null>(null);

  useEffect(() => {
    if (overlayContainer == null || firstClick !== null) {
      return undefined;
    }
    const onFirstClick = (event: LeafletEvent) => {
      // Leaflet's popup click handler stops the click here so it doesn't reach the map, where a
      // drawing tool would turn it into a new point/HPF. The real popup isn't bound yet, so do the
      // same. (@types/leaflet types `stop` as taking a DOM Event; Leaflet passes it Leaflet events.)
      DomEvent.stop((event as unknown) as Event);
      setFirstClick(event);
    };
    overlayContainer.once("click", onFirstClick);
    return () => {
      overlayContainer.off("click", onFirstClick);
    };
  }, [overlayContainer, firstClick]);

  return firstClick === null ? null : (
    <>
      <Popup {...popupOptions}>{renderContent()}</Popup>
      <ReplayClick event={firstClick} />
    </>
  );
};

export default LazyPopup;
